import React, { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Types of animations available
 */
type AnimationType = 'fade-in' | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right' | 'zoom-in' | 'zoom-out';

interface AnimatedContainerProps {
  children: ReactNode;
  animation?: AnimationType;
  delay?: number;
  duration?: number;
  className?: string;
}

/**
 * AnimatedContainer component
 * 
 * A reusable component that applies animations to its children
 * with configurable animation type, duration, and delay.
 * Supports various animation effects for progressive enhancement.
 */
const AnimatedContainer: React.FC<AnimatedContainerProps> = ({
  children,
  animation = 'fade-in',
  delay = 0,
  duration = 300,
  className
}) => {
  // Animation class mapping
  const animationClasses = {
    'fade-in': 'animate-fade-in',
    'slide-up': 'animate-slide-up',
    'slide-down': 'animate-slide-down',
    'slide-left': 'animate-slide-left',
    'slide-right': 'animate-slide-right',
    'zoom-in': 'animate-zoom-in',
    'zoom-out': 'animate-zoom-out'
  };

  // Apply animation style based on parameters
  const style = {
    '--animation-delay': `${delay}ms`,
    '--animation-duration': `${duration}ms`
  } as React.CSSProperties;

  return (
    <div 
      className={cn(animationClasses[animation], className)} 
      style={style}
    >
      {children}
    </div>
  );
};

export default AnimatedContainer;
